import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useKeyDownListener from 'hooks/useKeyDownListener';
import OrderOperatorsSvg from 'images/v5/icons/menu/order-operators.svg';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal } from 'store/layoutSlice';
import { addOrderOperator } from 'store/orderOperatorsSlice';
import useOrderOperatorValidation from '../useOrderOperatorValidation';
function OrderOperatorCreator() {
    const { t } = useTranslation(['specification', 'common', 'orderOperators']);
    const dispatch = useDispatch();
    const tenantId = useSelector((state) => { var _a; return (_a = state.user.tennant) === null || _a === void 0 ? void 0 : _a.id; });
    const isLoading = useSelector((state) => state.orderOperators.status.add === 'loading');
    const [orderOperatorForm, setOrderOperatorForm] = useState({
        name: '',
        vatNumber: '',
        countryOfOrigin: '',
        addressPostalCode: '',
        addressCity: '',
        addressStreet: '',
        addressNumber: '',
        emailAddress: '',
        contactPhone: '',
    });
    const { errors, isValid, pendingFields, markTouched, validateAll } = useOrderOperatorValidation(orderOperatorForm, t);
    const handleClose = () => {
        dispatch(closeModal({ name: 'orderOperatorCreator' }));
    };
    const handleVariantChange = ({ target: { name, value }, }) => {
        setOrderOperatorForm((form) => (Object.assign(Object.assign({}, form), { [name]: value })));
    };
    const handleFormSubmit = () => {
        if (!validateAll())
            return;
        dispatch(addOrderOperator(Object.assign({ tenantId }, orderOperatorForm)))
            .unwrap()
            .then(() => {
            dispatch(closeModal({ name: 'orderOperatorCreator' }));
        });
    };
    useKeyDownListener([
        { code: 'Enter', callback: handleFormSubmit },
        { code: 'Escape', callback: handleClose },
    ]);
    return (React.createElement(Modal, { size: "m", name: "orderOperatorCreator", header: React.createElement(Label, { weight: "strong" },
            React.createElement(OrderOperatorsSvg, null),
            " ",
            t('orderOperators:add_order_operator_capitalize')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: isLoading, pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleClose, validateAll: validateAll }) },
        React.createElement(Grid, { gap: 32 },
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:basic_info')),
                React.createElement(FormField, { direction: "row", label: t('orderOperators:order_operator_name_capitalize'), error: errors.name },
                    React.createElement(Input, { value: orderOperatorForm.name, onChange: handleVariantChange, onBlur: () => markTouched('name'), name: "name", error: Boolean(errors.name) })),
                React.createElement(FormField, { direction: "row", label: t('specification:vat_number') },
                    React.createElement(Input, { value: orderOperatorForm.vatNumber, onChange: handleVariantChange, name: "vatNumber" }))),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:address')),
                React.createElement(FormField, { direction: "row", label: t('specification:country_of_origin') },
                    React.createElement(Input, { value: orderOperatorForm.countryOfOrigin, onChange: handleVariantChange, name: "countryOfOrigin" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_postal_code') },
                    React.createElement(Input, { value: orderOperatorForm.addressPostalCode, onChange: handleVariantChange, name: "addressPostalCode" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_city') },
                    React.createElement(Input, { value: orderOperatorForm.addressCity, onChange: handleVariantChange, name: "addressCity" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_street') },
                    React.createElement(Input, { value: orderOperatorForm.addressStreet, onChange: handleVariantChange, name: "addressStreet" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_number') },
                    React.createElement(Input, { value: orderOperatorForm.addressNumber, onChange: handleVariantChange, name: "addressNumber" })),
                React.createElement(FormField, { direction: "row", label: t('specification:email_address') },
                    React.createElement(Input, { value: orderOperatorForm.emailAddress, onChange: handleVariantChange, name: "emailAddress" })),
                React.createElement(FormField, { direction: "row", label: t('specification:contact_phone') },
                    React.createElement(Input, { value: orderOperatorForm.contactPhone, onChange: handleVariantChange, name: "contactPhone" }))))));
}
export default memo(OrderOperatorCreator);
